import * as React from "react";
import classNames from "classnames";
import "./CPText-styles.scss";
import { CPTextProps } from "../../types";

/**
 * Typographic utility component. Renders styled text using CSS class tokens for scale, transform,
 * color, alignment, and font family. When multiple children are passed, each is wrapped in a
 * spacer `<div>` for consistent inline layout.
 *
 * @example
 * ```tsx
 * <CPText textStyle="h3" color="primary" bold>Section Heading</CPText>
 *
 * <CPText textStyle="caption" transform="uppercase" align="center" gutterBottom>
 *   Small uppercase label
 * </CPText>
 *
 * <CPText textStyle="body" noWrap>This text will not wrap to a new line.</CPText>
 * ```
 */
const CPText: React.FC<CPTextProps> = ({
  textStyle = "body",
  transform = "normal",
  style,
  children,
  color,
  align,
  font,
  gutterBottom,
  className,
  hidden,
  bold,
  fontSize,
  noWrap,
  title,
  ...rest
}) => {
  textStyle = textStyle ?? "body";
  transform = transform ?? "normal";
  align = align ?? "left";
  font = font ?? "default";
  gutterBottom = gutterBottom ?? false;

  const renderSpacers =
    React.Children.count(children) > 1 &&
    React.Children.toArray(children).some((child) =>
      React.isValidElement(child),
    );

  if (hidden) {
    return null;
  }

  return (
    <div
      {...rest}
      className={classNames(
        "cp-typography",
        textStyle,
        transform,
        color,
        align,
        font,
        className,
        { "cp-gutter": gutterBottom },
        { bold: bold },
        { noWrap: noWrap },
      )}
      style={{ fontSize }}
    >
      <span
        style={{
          textTransform:
            transform !== "normal"
              ? (transform as React.CSSProperties["textTransform"])
              : undefined,
          ...style,
        }}
        title={title}
      >
        {renderSpacers
          ? React.Children.map(children, (x, i) => (
              <div className="cp-typography-child" key={i}>
                {x}
              </div>
            ))
          : children}
      </span>
    </div>
  );
};

export default CPText;
